/**
 * Content Intelligence — Main Page
 * Flow: Workspace list → click → Workspace detail (Sources | Staging | Stats)
 */

import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ciService } from '../../services/ci.service'
import { getPlatformIcon, getStatusColor } from '../../types/contentIntelligence'
import toast from 'react-hot-toast'

type InnerTab = 'sources' | 'staging' | 'planner' | 'insights' | 'stats'

export default function CiPage() {
  const [selectedWsId, setSelectedWsId] = useState<number | null>(null)

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        {selectedWsId && (
          <button
            className="text-gray-400 hover:text-gray-700 transition-colors"
            onClick={() => setSelectedWsId(null)}
            title="Back to workspaces"
          >
            ← Back
          </button>
        )}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Content Intelligence</h1>
          <p className="text-gray-500 mt-1">AI-powered content creation from monitored sources</p>
        </div>
      </div>

      {selectedWsId ? (
        <WorkspaceView wsId={selectedWsId} onBack={() => setSelectedWsId(null)} />
      ) : (
        <WorkspacesTab onSelectWorkspace={setSelectedWsId} />
      )}
    </div>
  )
}

// ════════════════════════════════════════
// WORKSPACES LIST (top level)
// ════════════════════════════════════════

function WorkspacesTab({ onSelectWorkspace }: { onSelectWorkspace: (id: number) => void }) {
  const queryClient = useQueryClient()
  const [showCreate, setShowCreate] = useState(false)
  const [newName, setNewName] = useState('')
  const [newType, setNewType] = useState<'brand' | 'personal'>('brand')

  const { data: workspaces, isLoading } = useQuery({
    queryKey: ['ci-workspaces'],
    queryFn: () => ciService.getWorkspaces(),
  })

  const createMutation = useMutation({
    mutationFn: () => ciService.createWorkspace({ name: newName, type: newType }),
    onSuccess: (ws) => {
      queryClient.invalidateQueries({ queryKey: ['ci-workspaces'] })
      onSelectWorkspace(ws.id)
      setShowCreate(false)
      setNewName('')
      toast.success('Workspace created')
    },
    onError: () => toast.error('Failed to create workspace'),
  })

  if (isLoading) return <Skeleton rows={3} />

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">{workspaces?.length || 0} workspaces</p>
        <button className="btn-primary text-sm" onClick={() => setShowCreate(!showCreate)}>
          + New Workspace
        </button>
      </div>

      {showCreate && (
        <div className="card space-y-3">
          <input
            className="input-field"
            placeholder="Workspace name (e.g. My Brand, Personal Blog)"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
          <div className="flex gap-3">
            <label className="flex items-center gap-2 text-sm">
              <input type="radio" checked={newType === 'brand'} onChange={() => setNewType('brand')} />
              Brand
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="radio" checked={newType === 'personal'} onChange={() => setNewType('personal')} />
              Personal
            </label>
          </div>
          <div className="flex gap-2">
            <button
              className="btn-primary text-sm"
              disabled={!newName.trim() || createMutation.isPending}
              onClick={() => createMutation.mutate()}
            >
              {createMutation.isPending ? 'Creating...' : 'Create'}
            </button>
            <button className="btn-secondary text-sm" onClick={() => setShowCreate(false)}>Cancel</button>
          </div>
        </div>
      )}

      {!workspaces?.length && !showCreate ? (
        <EmptyState icon="🏢" title="No workspaces" description="Create your first workspace to start monitoring sources and generating content." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {workspaces?.map((ws) => (
            <div
              key={ws.id}
              onClick={() => onSelectWorkspace(ws.id)}
              className="card cursor-pointer transition-all hover:shadow-md hover:border-primary-300"
            >
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-semibold text-gray-900">{ws.name}</h3>
                  <span className="text-xs text-gray-500">{ws.type} workspace</span>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded ${getStatusColor(ws.status)}`}>
                  {ws.status}
                </span>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                <div className="bg-gray-50 rounded p-2">
                  <div className="text-xs text-gray-500">AI tokens</div>
                  <div className="font-medium text-sm">{ws.ai_tokens_today.toLocaleString()}</div>
                </div>
                <div className="bg-gray-50 rounded p-2">
                  <div className="text-xs text-gray-500">Limit</div>
                  <div className="font-medium text-sm">{(ws.daily_token_limit / 1000).toFixed(0)}k</div>
                </div>
                <div className="bg-gray-50 rounded p-2">
                  <div className="text-xs text-gray-500">Auto-approve</div>
                  <div className="font-medium text-sm">{ws.auto_approve ? 'On' : 'Off'}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ════════════════════════════════════════
// WORKSPACE VIEW (Sources | Staging | Stats)
// ════════════════════════════════════════

function WorkspaceView({ wsId, onBack }: { wsId: number; onBack: () => void }) {
  const [activeTab, setActiveTab] = useState<InnerTab>('sources')
  const queryClient = useQueryClient()

  const { data: workspace } = useQuery({
    queryKey: ['ci-workspace', wsId],
    queryFn: () => ciService.getWorkspaces().then(ws => ws.find(w => w.id === wsId)),
  })

  const archiveMutation = useMutation({
    mutationFn: () => ciService.archiveWorkspace(wsId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ci-workspaces'] })
      onBack()
      toast.success('Workspace archived')
    },
  })

  const tabs: { id: InnerTab; label: string; icon: string }[] = [
    { id: 'sources', label: 'Sources', icon: '📡' },
    { id: 'staging', label: 'Staging', icon: '📝' },
    { id: 'planner', label: 'Planner', icon: '📅' },
    { id: 'insights', label: 'Insights', icon: '🧠' },
    { id: 'stats', label: 'Stats', icon: '📊' },
  ]

  return (
    <div className="space-y-5">
      {/* Workspace header */}
      <div className="card flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900">{workspace?.name || '...'}</h2>
          <span className="text-xs text-gray-500">{workspace?.type} workspace</span>
        </div>
        <div className="flex items-center gap-4">
          {workspace && (
            <div className="flex gap-3 text-center text-xs">
              <div>
                <div className="font-bold text-sm">{workspace.ai_tokens_today.toLocaleString()}</div>
                <div className="text-gray-500">tokens today</div>
              </div>
              <div>
                <div className="font-bold text-sm">{(workspace.daily_token_limit / 1000).toFixed(0)}k</div>
                <div className="text-gray-500">limit</div>
              </div>
            </div>
          )}
          <button
            className="text-xs text-red-500 hover:text-red-700"
            onClick={() => archiveMutation.mutate()}
          >
            Archive
          </button>
        </div>
      </div>

      {/* Context panel */}
      <WorkspaceContext wsId={wsId} />

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'sources' && <SourcesTab wsId={wsId} />}
      {activeTab === 'staging' && <StagingTab wsId={wsId} />}
      {activeTab === 'planner' && <PlannerTab wsId={wsId} />}
      {activeTab === 'insights' && <InsightsTab wsId={wsId} />}
      {activeTab === 'stats' && <StatsTab wsId={wsId} />}
    </div>
  )
}

// ════════════════════════════════════════
// WORKSPACE CONTEXT (L1 + L3)
// ════════════════════════════════════════

function WorkspaceContext({ wsId }: { wsId: number }) {
  const queryClient = useQueryClient()
  const [contextText, setContextText] = useState('')
  const [showAddEntry, setShowAddEntry] = useState(false)
  const [entryType, setEntryType] = useState<string>('achievement')
  const [entryContent, setEntryContent] = useState('')
  const [entryTags, setEntryTags] = useState('')
  const [expanded, setExpanded] = useState(false)

  const { data: context } = useQuery({
    queryKey: ['ci-context', wsId],
    queryFn: () => ciService.getContext(wsId),
  })

  const { data: entries } = useQuery({
    queryKey: ['ci-entries', wsId],
    queryFn: () => ciService.getEntries(wsId),
  })

  const { data: channels } = useQuery({
    queryKey: ['ci-channels', wsId],
    queryFn: () => ciService.getChannels(wsId),
  })

  const uploadCtx = useMutation({
    mutationFn: () => ciService.uploadContext(wsId, contextText),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ci-context', wsId] })
      setContextText('')
      toast.success('Context uploaded, compressing...')
    },
  })

  const addEntry = useMutation({
    mutationFn: () => ciService.createEntry(wsId, { entry_type: entryType, content: entryContent, tags: entryTags || undefined }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ci-entries', wsId] })
      setEntryContent('')
      setEntryTags('')
      setShowAddEntry(false)
      toast.success('Entry added')
    },
  })

  const [showAddChannel, setShowAddChannel] = useState(false)
  const [chName, setChName] = useState('')
  const [chPlatform, setChPlatform] = useState<'telegram' | 'linkedin'>('telegram')
  const [chExternalId, setChExternalId] = useState('')

  const addChannel = useMutation({
    mutationFn: () => ciService.createChannel(wsId, { platform: chPlatform, name: chName, external_id: chExternalId || undefined }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ci-channels', wsId] })
      setChName('')
      setChExternalId('')
      setShowAddChannel(false)
      toast.success('Channel added')
    },
  })

  const hasContext = !!context?.compressed
  const entryCount = entries?.length || 0
  const channelCount = channels?.length || 0

  return (
    <div className="card">
      <div className="flex items-center justify-between cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center gap-3">
          <h4 className="font-semibold text-gray-900">Workspace Setup</h4>
          <div className="flex gap-2 text-xs">
            <span className={`px-2 py-0.5 rounded ${hasContext ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
              {hasContext ? '✓ Context' : '✗ No context'}
            </span>
            <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{entryCount} entries</span>
            <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{channelCount} channels</span>
          </div>
        </div>
        <span className="text-gray-400">{expanded ? '▲' : '▼'}</span>
      </div>

      {expanded && (
        <div className="mt-4 space-y-4">
          {/* L1 Context */}
          <div>
            <h5 className="text-sm font-medium text-gray-700 mb-2">Brand Context (L1)</h5>
            {context?.compressed ? (
              <div>
                <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded">{context.compressed.slice(0, 300)}...</p>
                <p className="text-xs text-gray-400 mt-1">{context.token_count ?? '?'} tokens</p>
              </div>
            ) : (
              <div className="space-y-2">
                <textarea
                  className="input-field h-24"
                  placeholder="Describe your brand, product, target audience, positioning..."
                  value={contextText}
                  onChange={(e) => setContextText(e.target.value)}
                />
                <button
                  className="btn-primary text-sm"
                  disabled={contextText.length < 10 || uploadCtx.isPending}
                  onClick={() => uploadCtx.mutate()}
                >
                  {uploadCtx.isPending ? 'Uploading...' : 'Upload & Compress'}
                </button>
              </div>
            )}
          </div>

          {/* L3 Entries */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h5 className="text-sm font-medium text-gray-700">Achievements & Cases (L3)</h5>
              <button className="text-xs text-primary-600 hover:text-primary-700" onClick={() => setShowAddEntry(!showAddEntry)}>+ Add</button>
            </div>
            {showAddEntry && (
              <div className="bg-gray-50 rounded p-3 mb-3 space-y-2">
                <select className="input-field text-sm" value={entryType} onChange={(e) => setEntryType(e.target.value)}>
                  <option value="achievement">Achievement</option>
                  <option value="metric">Metric</option>
                  <option value="case">Case Study</option>
                  <option value="quote">Quote</option>
                </select>
                <input className="input-field text-sm" placeholder="Content (e.g. MRR grew to $12k)" value={entryContent} onChange={(e) => setEntryContent(e.target.value)} />
                <input className="input-field text-sm" placeholder="Tags (comma-separated)" value={entryTags} onChange={(e) => setEntryTags(e.target.value)} />
                <button className="btn-primary text-sm" disabled={!entryContent.trim() || addEntry.isPending} onClick={() => addEntry.mutate()}>
                  {addEntry.isPending ? 'Adding...' : 'Add Entry'}
                </button>
              </div>
            )}
            {entries?.length ? (
              <div className="space-y-1">
                {entries.map((e) => (
                  <div key={e.id} className="flex items-start gap-2 text-sm bg-gray-50 rounded p-2">
                    <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded flex-shrink-0">{e.entry_type}</span>
                    <span className="text-gray-700">{e.content}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-gray-400">No entries yet</p>
            )}
          </div>

          {/* Output Channels */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h5 className="text-sm font-medium text-gray-700">Output Channels</h5>
              <button className="text-xs text-primary-600 hover:text-primary-700" onClick={() => setShowAddChannel(!showAddChannel)}>+ Add Channel</button>
            </div>
            {showAddChannel && (
              <div className="bg-gray-50 rounded p-3 mb-3 space-y-2">
                <div className="flex gap-2">
                  <select className="input-field text-sm flex-1" value={chPlatform} onChange={(e) => setChPlatform(e.target.value as 'telegram' | 'linkedin')}>
                    <option value="telegram">Telegram</option>
                    <option value="linkedin">LinkedIn</option>
                  </select>
                  <input className="input-field text-sm flex-[2]" placeholder="Channel name" value={chName} onChange={(e) => setChName(e.target.value)} />
                </div>
                <input className="input-field text-sm" placeholder="External ID (@username or channel ID)" value={chExternalId} onChange={(e) => setChExternalId(e.target.value)} />
                <button className="btn-primary text-sm" disabled={!chName.trim() || addChannel.isPending} onClick={() => addChannel.mutate()}>
                  {addChannel.isPending ? 'Adding...' : 'Add'}
                </button>
              </div>
            )}
            {channels?.length ? (
              <div className="space-y-1">
                {channels.map((ch) => (
                  <ChannelRow key={ch.id} channel={ch} wsId={wsId} />
                ))}
              </div>
            ) : (
              <p className="text-xs text-gray-400">No output channels</p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ════════════════════════════════════════
// CHANNEL ROW WITH SCHEDULE EDITOR
// ════════════════════════════════════════

const DAYS = [
  { key: 0, label: 'Mon' }, { key: 1, label: 'Tue' }, { key: 2, label: 'Wed' },
  { key: 3, label: 'Thu' }, { key: 4, label: 'Fri' }, { key: 5, label: 'Sat' }, { key: 6, label: 'Sun' },
]

type ScheduleSlot = { dow: number[]; times: string[] }

function parseSlots(raw: string | null | undefined): ScheduleSlot[] {
  if (!raw) return []
  try { return JSON.parse(raw) } catch { return [] }
}

function formatSchedule(raw: string | null | undefined): string {
  const slots = parseSlots(raw)
  if (!slots.length) return 'No schedule'
  return slots.map(s => {
    const days = s.dow.map(d => DAYS[d]?.label).join(', ')
    return `${days} @ ${s.times.join(', ')}`
  }).join(' | ')
}

function ChannelRow({ channel, wsId }: { channel: { id: number; platform: string; name: string; lang: string; tone: string; status: string; schedule_slots?: string | null }; wsId: number }) {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [selectedDays, setSelectedDays] = useState<number[]>([1, 3, 5])
  const [times, setTimes] = useState('10:00, 18:00')

  const saveMutation = useMutation({
    mutationFn: () => {
      const slots: ScheduleSlot[] = [{ dow: selectedDays, times: times.split(',').map(t => t.trim()).filter(Boolean) }]
      return ciService.updateChannel(wsId, channel.id, { schedule_slots: JSON.stringify(slots) } as any)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ci-channels', wsId] })
      toast.success('Schedule saved')
      setOpen(false)
    },
  })

  // Init from existing schedule
  const initFromExisting = () => {
    const slots = parseSlots(channel.schedule_slots)
    if (slots.length) {
      setSelectedDays(slots[0].dow)
      setTimes(slots[0].times.join(', '))
    }
    setOpen(true)
  }

  return (
    <div className="bg-gray-50 rounded p-2 text-sm">
      <div className="flex items-center justify-between cursor-pointer" onClick={initFromExisting}>
        <div className="flex items-center gap-2">
          <span>{getPlatformIcon(channel.platform)}</span>
          <span className="font-medium">{channel.name}</span>
          <span className="text-xs text-gray-400">{channel.lang} / {channel.tone}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">{formatSchedule(channel.schedule_slots)}</span>
          <span className={`text-xs px-2 py-0.5 rounded ${getStatusColor(channel.status)}`}>{channel.status}</span>
        </div>
      </div>

      {open && (
        <div className="mt-3 pt-3 border-t space-y-3">
          <h6 className="text-xs font-medium text-gray-700">Publishing Schedule</h6>

          {/* Day selector */}
          <div className="flex gap-1">
            {DAYS.map((d) => (
              <button
                key={d.key}
                className={`px-2 py-1 text-xs rounded transition-colors ${
                  selectedDays.includes(d.key)
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                }`}
                onClick={() =>
                  setSelectedDays(prev =>
                    prev.includes(d.key)
                      ? prev.filter(x => x !== d.key)
                      : [...prev, d.key].sort()
                  )
                }
              >
                {d.label}
              </button>
            ))}
          </div>

          {/* Time slots */}
          <div>
            <label className="text-xs text-gray-500 block mb-1">Times (comma-separated, 24h)</label>
            <input
              className="input-field text-sm"
              placeholder="10:00, 14:00, 18:00"
              value={times}
              onChange={(e) => setTimes(e.target.value)}
            />
          </div>

          <div className="flex gap-2">
            <button
              className="btn-primary text-xs"
              disabled={!selectedDays.length || !times.trim() || saveMutation.isPending}
              onClick={() => saveMutation.mutate()}
            >
              {saveMutation.isPending ? 'Saving...' : 'Save Schedule'}
            </button>
            <button className="btn-secondary text-xs" onClick={() => setOpen(false)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  )
}

// ════════════════════════════════════════
// SOURCES TAB (inside workspace)
// ════════════════════════════════════════

function SourcesTab({ wsId }: { wsId: number }) {
  const queryClient = useQueryClient()
  const [showAdd, setShowAdd] = useState(false)
  const [srcName, setSrcName] = useState('')
  const [srcPlatform, setSrcPlatform] = useState<'telegram' | 'twitter' | 'linkedin'>('telegram')
  const [srcExtId, setSrcExtId] = useState('')

  const { data: allSources, isLoading } = useQuery({
    queryKey: ['ci-sources'],
    queryFn: () => ciService.getSources(),
  })

  const { data: wsSources } = useQuery({
    queryKey: ['ci-ws-sources', wsId],
    queryFn: () => ciService.getWorkspaceSources(wsId),
  })

  const { data: posts } = useQuery({
    queryKey: ['ci-posts', wsId],
    queryFn: () => ciService.getPosts(wsId, { limit: 20 }),
  })

  const createSource = useMutation({
    mutationFn: () => ciService.createSource({ name: srcName, platform: srcPlatform, external_id: srcExtId || undefined }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ci-sources'] })
      setSrcName('')
      setSrcExtId('')
      setShowAdd(false)
      toast.success('Source created')
    },
  })

  const subscribeMutation = useMutation({
    mutationFn: (sourceId: number) => ciService.subscribeSource(wsId, sourceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ci-ws-sources', wsId] })
      toast.success('Source subscribed')
    },
    onError: () => toast.error('Already subscribed or error'),
  })

  if (isLoading) return <Skeleton rows={4} />

  const wsSourceIds = new Set(wsSources?.map(s => s.id) || [])

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">{allSources?.length || 0} sources total, {wsSources?.length || 0} subscribed</p>
        <button className="btn-primary text-sm" onClick={() => setShowAdd(!showAdd)}>+ Add Source</button>
      </div>

      {showAdd && (
        <div className="card space-y-2">
          <div className="flex gap-2">
            <select className="input-field text-sm flex-1" value={srcPlatform} onChange={(e) => setSrcPlatform(e.target.value as 'telegram' | 'twitter' | 'linkedin')}>
              <option value="telegram">Telegram</option>
              <option value="twitter">Twitter/X</option>
              <option value="linkedin">LinkedIn</option>
            </select>
            <input className="input-field text-sm flex-[2]" placeholder="Source name" value={srcName} onChange={(e) => setSrcName(e.target.value)} />
          </div>
          <input className="input-field text-sm" placeholder="External ID (@channel, user_id, company_id)" value={srcExtId} onChange={(e) => setSrcExtId(e.target.value)} />
          <div className="flex gap-2">
            <button className="btn-primary text-sm" disabled={!srcName.trim() || createSource.isPending} onClick={() => createSource.mutate()}>Create</button>
            <button className="btn-secondary text-sm" onClick={() => setShowAdd(false)}>Cancel</button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {allSources?.map((src) => (
          <div key={src.id} className="card flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-xl">{getPlatformIcon(src.platform)}</span>
              <div>
                <div className="font-medium text-sm">{src.name}</div>
                <div className="text-xs text-gray-400">{src.external_id || 'no ID'} · {src.posts_7d} posts/7d</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-xs px-2 py-0.5 rounded ${getStatusColor(src.status)}`}>{src.status}</span>
              {!wsSourceIds.has(src.id) ? (
                <button className="text-xs text-primary-600 hover:text-primary-700" onClick={() => subscribeMutation.mutate(src.id)}>Subscribe</button>
              ) : (
                <span className="text-xs text-green-600">✓ Subscribed</span>
              )}
            </div>
          </div>
        ))}
      </div>

      {posts && posts.length > 0 && (
        <div className="mt-4">
          <h4 className="font-semibold text-gray-900 mb-3">Collected Posts</h4>
          <div className="space-y-2">
            {posts.map((p) => (
              <div key={p.id} className="card text-sm">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <p className="text-gray-700 line-clamp-2">{p.text?.slice(0, 200) || '(media only)'}</p>
                    <div className="flex gap-3 mt-1 text-xs text-gray-400">
                      {p.topic && <span>Topic: {p.topic}</span>}
                      {p.ai_score != null && <span>Score: {p.ai_score}</span>}
                      <span>{p.views} views</span>
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded ml-2 flex-shrink-0 ${getStatusColor(p.analysis_status)}`}>
                    {p.analysis_status}
                    {p.skip_reason && ` (${p.skip_reason})`}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ════════════════════════════════════════
// STAGING TAB (enhanced with comment, defer, inline edit)
// ════════════════════════════════════════

function StagingTab({ wsId }: { wsId: number }) {
  const queryClient = useQueryClient()
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editText, setEditText] = useState('')
  const [commentId, setCommentId] = useState<number | null>(null)
  const [comment, setComment] = useState('')
  const [deferId, setDeferId] = useState<number | null>(null)
  const [deferDate, setDeferDate] = useState('')

  const { data: staging, isLoading } = useQuery({
    queryKey: ['ci-staging', wsId],
    queryFn: () => ciService.getStaging(wsId),
  })

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['ci-staging', wsId] })

  const approveMut = useMutation({
    mutationFn: (id: number) => ciService.approvePost(id),
    onSuccess: () => { invalidate(); toast.success('Post approved') },
  })

  const rejectMut = useMutation({
    mutationFn: ({ id, comment }: { id: number; comment: string }) =>
      ciService.rejectPost(id, comment, comment),
    onSuccess: () => { invalidate(); setCommentId(null); setComment(''); toast.success('Post rejected') },
  })

  const editMut = useMutation({
    mutationFn: ({ id, text, comment }: { id: number; text: string; comment: string }) =>
      ciService.editPost(id, text, comment),
    onSuccess: () => { invalidate(); setEditingId(null); setEditText(''); toast.success('Post saved') },
  })

  const deferMut = useMutation({
    mutationFn: ({ id, until, comment }: { id: number; until: string; comment: string }) =>
      ciService.deferPost(id, until, comment),
    onSuccess: () => { invalidate(); setDeferId(null); setDeferDate(''); setComment(''); toast.success('Post deferred') },
  })

  const regenMut = useMutation({
    mutationFn: (id: number) => ciService.regeneratePost(id),
    onSuccess: () => toast.success('Regeneration queued'),
  })

  if (isLoading) return <Skeleton rows={3} />

  if (!staging?.length) {
    return <EmptyState icon="📝" title="No posts in staging" description="Posts will appear here after sources are collected and analyzed, or after the planner runs." />
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-500">{staging.length} posts in staging</p>
      {staging.map((post) => (
        <div key={post.id} className={`card ${post.context_stale ? 'border-yellow-300 ring-1 ring-yellow-200' : ''}`}>
          {/* Badges row */}
          <div className="flex gap-2 mb-2 text-xs">
            <span className={`px-2 py-0.5 rounded ${getStatusColor(post.status)}`}>{post.status}</span>
            {post.generation_source === 'planner' && (
              <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-700">📅 planner</span>
            )}
            {post.context_stale && (
              <span className="px-2 py-0.5 rounded bg-yellow-100 text-yellow-700">⚠️ stale context</span>
            )}
            {post.scheduled_for && (
              <span className="text-gray-400">📆 {new Date(post.scheduled_for).toLocaleString()}</span>
            )}
          </div>

          {/* Post text — inline editable */}
          {editingId === post.id ? (
            <div className="space-y-2">
              <textarea
                className="input-field text-sm h-32"
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
              />
              <input
                className="input-field text-sm"
                placeholder="Comment (optional) — why you changed it"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
              <div className="flex gap-2">
                <button className="btn-primary text-xs" onClick={() => editMut.mutate({ id: post.id, text: editText, comment })}>
                  Save
                </button>
                <button className="btn-secondary text-xs" onClick={() => setEditingId(null)}>Cancel</button>
              </div>
            </div>
          ) : (
            <p
              className="text-sm text-gray-800 whitespace-pre-line cursor-pointer hover:bg-gray-50 rounded p-1 -m-1"
              onClick={() => { setEditingId(post.id); setEditText(post.text_edited || post.text); setComment('') }}
              title="Click to edit"
            >
              {post.text_edited || post.text}
            </p>
          )}

          {/* Action buttons */}
          {editingId !== post.id && (
            <div className="flex items-center justify-between mt-3 pt-3 border-t">
              <div className="text-xs text-gray-400">
                {post.ai_model && <span>{post.ai_model}</span>}
              </div>
              <div className="flex gap-2">
                {post.context_stale && (
                  <button className="text-xs text-yellow-600 hover:text-yellow-700" onClick={() => regenMut.mutate(post.id)}>Regenerate</button>
                )}
                {post.status === 'pending_approval' && (
                  <>
                    <button className="text-xs text-green-600 hover:text-green-700 font-medium" onClick={() => approveMut.mutate(post.id)}>✓ Approve</button>
                    <button
                      className="text-xs text-gray-500 hover:text-gray-700"
                      onClick={() => { setDeferId(deferId === post.id ? null : post.id); setDeferDate(''); setComment('') }}
                    >
                      ⏸ Defer
                    </button>
                    <button
                      className="text-xs text-red-500 hover:text-red-700"
                      onClick={() => { setCommentId(commentId === post.id ? null : post.id); setComment('') }}
                    >
                      ✗ Reject
                    </button>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Reject with comment */}
          {commentId === post.id && (
            <div className="mt-2 pt-2 border-t space-y-2">
              <input className="input-field text-sm" placeholder="Why are you rejecting? (helps AI learn)" value={comment} onChange={(e) => setComment(e.target.value)} />
              <button className="btn-primary text-xs bg-red-600 hover:bg-red-700" onClick={() => rejectMut.mutate({ id: post.id, comment })}>
                Reject with feedback
              </button>
            </div>
          )}

          {/* Defer with date */}
          {deferId === post.id && (
            <div className="mt-2 pt-2 border-t space-y-2">
              <input type="datetime-local" className="input-field text-sm" value={deferDate} onChange={(e) => setDeferDate(e.target.value)} />
              <input className="input-field text-sm" placeholder="Comment (optional)" value={comment} onChange={(e) => setComment(e.target.value)} />
              <button
                className="btn-primary text-xs"
                disabled={!deferDate}
                onClick={() => deferMut.mutate({ id: post.id, until: new Date(deferDate).toISOString(), comment })}
              >
                Defer until selected date
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

// ════════════════════════════════════════
// PLANNER TAB
// ════════════════════════════════════════

function PlannerTab({ wsId }: { wsId: number }) {
  const queryClient = useQueryClient()

  const { data: status, isLoading } = useQuery({
    queryKey: ['ci-planner-status', wsId],
    queryFn: () => ciService.getPlannerStatus(wsId),
    refetchInterval: 30000,
  })

  const { data: channels } = useQuery({
    queryKey: ['ci-channels', wsId],
    queryFn: () => ciService.getChannels(wsId),
  })

  const runMut = useMutation({
    mutationFn: () => ciService.runPlanner(wsId),
    onSuccess: () => {
      toast.success('Planner started — posts will appear in Staging')
      setTimeout(() => queryClient.invalidateQueries({ queryKey: ['ci-planner-status', wsId] }), 10000)
    },
    onError: () => toast.error('Failed to start planner'),
  })

  const updateChannel = useMutation({
    mutationFn: ({ chId, ppw }: { chId: number; ppw: number }) =>
      ciService.updateChannel(wsId, chId, { posts_per_week: ppw } as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ci-planner-status', wsId] })
      queryClient.invalidateQueries({ queryKey: ['ci-channels', wsId] })
      toast.success('Updated')
    },
  })

  if (isLoading) return <Skeleton rows={3} />

  const channelStatuses = status?.channels || []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-semibold text-gray-900">Content Planner</h4>
          <p className="text-sm text-gray-500">Auto-generates posts to meet your weekly targets. Runs every 6 hours.</p>
        </div>
        <button
          className="btn-primary text-sm"
          disabled={runMut.isPending}
          onClick={() => runMut.mutate()}
        >
          {runMut.isPending ? '⏳ Running...' : '▶ Run Planner Now'}
        </button>
      </div>

      {/* Channel targets */}
      {channelStatuses.length === 0 ? (
        <EmptyState icon="📅" title="No channels configured" description="Add output channels in Workspace Setup first." />
      ) : (
        <div className="space-y-3">
          {channelStatuses.map((ch: any) => {
            const channelData = channels?.find((c: any) => c.id === ch.channel_id)
            return (
              <div key={ch.channel_id} className="card">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span>{getPlatformIcon(ch.platform)}</span>
                    <span className="font-medium">{ch.channel_name}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 text-sm">
                      <label className="text-xs text-gray-500">Posts/week:</label>
                      <input
                        type="number"
                        min={1}
                        max={30}
                        className="input-field text-sm w-16 text-center"
                        value={channelData?.posts_per_week ?? ch.posts_per_week}
                        onChange={(e) => {
                          const v = parseInt(e.target.value)
                          if (v >= 1 && v <= 30) updateChannel.mutate({ chId: ch.channel_id, ppw: v })
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Pipeline bar */}
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                      <span>Pipeline: {ch.pipeline_count} / {ch.posts_per_week}</span>
                      {ch.deficit > 0 && (
                        <span className="text-red-600 font-medium">Deficit: {ch.deficit} posts</span>
                      )}
                      {ch.deficit === 0 && (
                        <span className="text-green-600 font-medium">✓ On track</span>
                      )}
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${
                          ch.deficit > 0 ? 'bg-red-400' : 'bg-green-500'
                        }`}
                        style={{ width: `${Math.min(100, (ch.pipeline_count / Math.max(1, ch.posts_per_week)) * 100)}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* How it works */}
      <div className="bg-gray-50 rounded-lg p-4 text-xs text-gray-500 space-y-1">
        <div className="font-medium text-gray-700 mb-1">How the planner works:</div>
        <p>1. Every 6h, checks how many posts are in your staging + scheduled pipeline</p>
        <p>2. If deficit (fewer than posts_per_week), generates new posts from best unused sources</p>
        <p>3. Posts appear in Staging for your review (approve / edit / reject / defer)</p>
        <p>4. Your rejections and edits feed back into AI — future posts get better over time</p>
      </div>
    </div>
  )
}

// ════════════════════════════════════════
// INSIGHTS TAB (Style + Topic Map + News Radar)
// ════════════════════════════════════════

function InsightsTab({ wsId }: { wsId: number }) {
  const queryClient = useQueryClient()
  const [postsText, setPostsText] = useState('')

  // Style profile
  const { data: styleData } = useQuery({
    queryKey: ['ci-style', wsId],
    queryFn: () => ciService.getStyle(wsId),
  })

  // Topic map
  const { data: topicData } = useQuery({
    queryKey: ['ci-topic-map', wsId],
    queryFn: () => ciService.getTopicMap(wsId),
  })

  // News radar results (stored in state, not persisted)
  const [newsItems, setNewsItems] = useState<any[]>([])

  const analyzeStyle = useMutation({
    mutationFn: () => ciService.analyzeStyle(wsId, postsText),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ci-style', wsId] })
      setPostsText('')
      toast.success('Style analyzed!')
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Error analyzing style'),
  })

  const buildMap = useMutation({
    mutationFn: () => ciService.buildTopicMap(wsId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ci-topic-map', wsId] })
      toast.success('Topic map built!')
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Error building topic map'),
  })

  const scanNews = useMutation({
    mutationFn: () => ciService.scanNews(wsId),
    onSuccess: (data) => {
      setNewsItems(data.items || [])
      toast.success(`${data.items?.length || 0} ideas generated`)
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Error scanning news'),
  })

  const style = styleData?.profile
  const topicMap = topicData?.topic_map

  return (
    <div className="space-y-6">
      {/* ── STYLE CLONING ── */}
      <div className="card">
        <h4 className="font-semibold text-gray-900 mb-3">🎨 Style Cloning</h4>
        {style ? (
          <div className="space-y-3">
            <div className="bg-green-50 border border-green-200 rounded p-3">
              <div className="flex items-center gap-2 text-sm text-green-700 font-medium mb-2">✓ Style profile extracted</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                <div>
                  <div className="text-xs text-gray-500 mb-1">Tone</div>
                  <div className="font-medium">{style.tone?.overall || '—'}</div>
                  <div className="text-xs text-gray-400">Emoji: {style.tone?.emoji_usage || '—'}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Language</div>
                  <div className="font-medium">{style.language?.formality || '—'}</div>
                  <div className="text-xs text-gray-400">{style.language?.sentence_style || '—'}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Structure</div>
                  <div className="font-medium">~{style.structure?.avg_length_words || '?'} words</div>
                  <div className="text-xs text-gray-400">{style.structure?.paragraphs_typical || '?'} paragraphs</div>
                </div>
              </div>
              {style.examples?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-green-200">
                  <div className="text-xs text-gray-500 mb-1">Characteristic phrases:</div>
                  <div className="flex flex-wrap gap-1">
                    {style.examples.map((ex: string, i: number) => (
                      <span key={i} className="text-xs bg-white px-2 py-0.5 rounded border">"{ex}"</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <button
              className="text-xs text-primary-600 hover:text-primary-700"
              onClick={() => { queryClient.setQueryData(['ci-style', wsId], { profile: null }) }}
            >
              Re-analyze with new posts
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-sm text-gray-500">Paste 10+ of your existing posts below. AI will extract your writing style.</p>
            <textarea
              className="input-field h-40 text-sm"
              placeholder="Paste your posts here, separated by blank lines..."
              value={postsText}
              onChange={(e) => setPostsText(e.target.value)}
            />
            <button
              className="btn-primary text-sm"
              disabled={postsText.length < 200 || analyzeStyle.isPending}
              onClick={() => analyzeStyle.mutate()}
            >
              {analyzeStyle.isPending ? '🧠 Analyzing...' : '🎨 Analyze My Style'}
            </button>
            <p className="text-xs text-gray-400">Minimum 200 characters. More posts = better analysis.</p>
          </div>
        )}
      </div>

      {/* ── TOPIC MAP ── */}
      <div className="card">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-gray-900">📍 Topic Map</h4>
          <button
            className="btn-primary text-xs"
            disabled={buildMap.isPending}
            onClick={() => buildMap.mutate()}
          >
            {buildMap.isPending ? 'Building...' : topicMap ? 'Rebuild' : 'Build from Posts'}
          </button>
        </div>

        {topicMap ? (
          <div className="space-y-3">
            {/* Categories */}
            {topicMap.categories?.map((cat: any, i: number) => (
              <div key={i} className="bg-gray-50 rounded p-3">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-sm">{cat.name}</span>
                  <span className={`text-xs px-2 py-0.5 rounded ${
                    cat.frequency === 'high' ? 'bg-green-100 text-green-700' :
                    cat.frequency === 'medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600'
                  }`}>{cat.frequency}</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">{cat.description}</p>
                {cat.keywords && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {cat.keywords.slice(0, 6).map((kw: string) => (
                      <span key={kw} className="text-xs bg-white px-1.5 py-0.5 rounded border">{kw}</span>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {/* Trending & Stop list */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {topicMap.trending?.length > 0 && (
                <div className="bg-green-50 rounded p-3">
                  <div className="text-xs font-medium text-green-700 mb-1">🔥 Trending</div>
                  <ul className="text-xs text-green-800 space-y-0.5">{topicMap.trending.map((t: string) => <li key={t}>• {t}</li>)}</ul>
                </div>
              )}
              {topicMap.stop_list?.length > 0 && (
                <div className="bg-red-50 rounded p-3">
                  <div className="text-xs font-medium text-red-700 mb-1">🚫 Stop-list</div>
                  <ul className="text-xs text-red-800 space-y-0.5">{topicMap.stop_list.map((t: string) => <li key={t}>• {t}</li>)}</ul>
                </div>
              )}
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-400">Build a topic map from your collected posts. Need at least 5 analyzed posts.</p>
        )}
      </div>

      {/* ── NEWS RADAR ── */}
      <div className="card">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-gray-900">📡 News Radar</h4>
          <button
            className="btn-primary text-xs"
            disabled={!topicMap || scanNews.isPending}
            onClick={() => scanNews.mutate()}
          >
            {scanNews.isPending ? '🔍 Scanning...' : '🔍 Generate Ideas'}
          </button>
        </div>

        {!topicMap && (
          <p className="text-sm text-gray-400">Build a topic map first — news radar uses it to find relevant ideas.</p>
        )}

        {newsItems.length > 0 && (
          <div className="space-y-2">
            {newsItems.map((item: any, i: number) => (
              <div key={i} className="bg-gray-50 rounded p-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="font-medium text-sm">{item.title}</div>
                    <p className="text-xs text-gray-500 mt-1">{item.summary}</p>
                    {item.post_angle && (
                      <div className="text-xs text-primary-600 mt-1">
                        💡 Angle: {item.post_angle}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1 ml-3 flex-shrink-0">
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      item.urgency === 'hot' ? 'bg-red-100 text-red-700' :
                      item.urgency === 'warm' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600'
                    }`}>{item.urgency}</span>
                    {item.category && <span className="text-xs text-gray-400">{item.category}</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ════════════════════════════════════════
// STATS TAB (inside workspace)
// ════════════════════════════════════════

function StatsTab({ wsId }: { wsId: number }) {
  const [period, setPeriod] = useState<'today' | '7d' | '30d'>('today')

  const { data: stats, isLoading: loadingStats } = useQuery({
    queryKey: ['ci-stats', wsId],
    queryFn: () => ciService.getStats(wsId),
  })

  const { data: usage, isLoading: loadingUsage } = useQuery({
    queryKey: ['ci-usage', wsId, period],
    queryFn: () => ciService.getAiUsage(wsId, period),
  })

  if (loadingStats) return <Skeleton rows={2} />

  return (
    <div className="space-y-6">
      {stats && (
        <div className="card">
          <h4 className="font-semibold text-gray-900 mb-3">Content Funnel</h4>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
            {([
              ['Collected', stats.collected, 'bg-gray-50'],
              ['Analyzed', stats.analyzed, 'bg-blue-50'],
              ['Passed', stats.passed_filter, 'bg-indigo-50'],
              ['Generated', stats.generated, 'bg-purple-50'],
              ['Approved', stats.approved, 'bg-green-50'],
              ['Published', stats.published, 'bg-green-100'],
            ] as const).map(([label, value, bg]) => (
              <div key={label} className={`${bg} rounded-lg p-3 text-center`}>
                <div className="text-lg font-bold text-gray-900">{value}</div>
                <div className="text-xs text-gray-500">{label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-gray-900">AI Usage</h4>
          <div className="flex gap-1">
            {(['today', '7d', '30d'] as const).map((p) => (
              <button
                key={p}
                className={`text-xs px-2 py-1 rounded ${period === p ? 'bg-primary-100 text-primary-700' : 'text-gray-500 hover:bg-gray-100'}`}
                onClick={() => setPeriod(p)}
              >
                {p}
              </button>
            ))}
          </div>
        </div>

        {loadingUsage ? <Skeleton rows={1} /> : usage && (
          <div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-gray-50 rounded p-3">
                <div className="text-xs text-gray-500">Tokens In</div>
                <div className="font-bold">{usage.tokens_in.toLocaleString()}</div>
              </div>
              <div className="bg-gray-50 rounded p-3">
                <div className="text-xs text-gray-500">Tokens Out</div>
                <div className="font-bold">{usage.tokens_out.toLocaleString()}</div>
              </div>
              <div className="bg-gray-50 rounded p-3">
                <div className="text-xs text-gray-500">Total</div>
                <div className="font-bold">{usage.tokens_total.toLocaleString()}</div>
              </div>
              <div className="bg-gray-50 rounded p-3">
                <div className="text-xs text-gray-500">Cost</div>
                <div className="font-bold">${usage.cost_usd.toFixed(4)}</div>
              </div>
            </div>
            {Object.keys(usage.breakdown).length > 0 && (
              <div className="mt-3 space-y-1">
                {Object.entries(usage.breakdown).map(([type, data]) => (
                  <div key={type} className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">{type}</span>
                    <span className="text-gray-900">{data.tokens.toLocaleString()} tokens (${data.cost_usd.toFixed(4)})</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {stats && (
          <div className="mt-3">
            <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
              <span>Daily limit</span>
              <span>{stats.ai_tokens_today.toLocaleString()} used</span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${
                  stats.ai_tokens_today / 100000 > 0.95 ? 'bg-red-500' :
                  stats.ai_tokens_today / 100000 > 0.8 ? 'bg-yellow-500' : 'bg-green-500'
                }`}
                style={{ width: `${Math.min(100, (stats.ai_tokens_today / 100000) * 100)}%` }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ════════════════════════════════════════
// SHARED COMPONENTS
// ════════════════════════════════════════

function Skeleton({ rows }: { rows: number }) {
  return (
    <div className="animate-pulse space-y-4">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="h-20 bg-gray-100 rounded-lg" />
      ))}
    </div>
  )
}

function EmptyState({ icon, title, description }: { icon: string; title: string; description: string }) {
  return (
    <div className="card text-center py-12">
      <div className="text-4xl mb-4">{icon}</div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">{title}</h3>
      <p className="text-gray-500 text-sm">{description}</p>
    </div>
  )
}
